import React, {
  useState,
  useEffect,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useOrganization } from "../context/OrganizationContext";
import apiService from "../services/apiService";
import toast from "react-hot-toast";
import DashboardUsageStatistics from "./DashboardUsageStatistics";
import DashboardInsights from "./DashboardInsights";
import {
  resolveChainLogoCandidates,
  resolveChainLogoUrl,
} from "../utils/chainLogoResolver";

function getCreateApiKeyErrorMessage(error) {
  const d = error?.response?.data;
  if (typeof d === "string") return d;
  if (d?.non_field_errors && Array.isArray(d.non_field_errors)) {
    return d.non_field_errors.filter(Boolean).join(" ");
  }
  if (d?.detail) {
    if (Array.isArray(d.detail))
      return d.detail
        .map((x) => (typeof x === "string" ? x : x?.msg || String(x)))
        .join(" ");
    return String(d.detail);
  }
  if (d?.name) return Array.isArray(d.name) ? d.name[0] : String(d.name);
  if (d && typeof d === "object") {
    const vals = Object.values(d).flat();
    const first = vals.find((v) => typeof v === "string");
    if (first) return first;
  }
  if (typeof error?.message === "string") return error.message;
  return "Failed to create API key";
}

function isSubscriptionRequiredError(error) {
  const d = error?.response?.data;
  const fromNonField =
    Array.isArray(d?.non_field_errors) &&
    d.non_field_errors.some((s) => /subscription|plan/i.test(String(s)));
  const msg = getCreateApiKeyErrorMessage(error);
  const fromMsg = /subscription|active subscription|plan/i.test(msg);
  return fromNonField || fromMsg;
}

/** Collapsed grid: 4 rows × 6 columns (xl) — adjust if grid breakpoints change. */
const NETWORKS_GRID_MAX_ROWS = 4;
const NETWORKS_GRID_COLS = 6;
const NETWORKS_COLLAPSED_COUNT = NETWORKS_GRID_MAX_ROWS * NETWORKS_GRID_COLS;

function AvailableChainCard({ chainAccess }) {
  const chain = chainAccess.chain_details || chainAccess.chain || {};
  const name = chain.name || "Unknown Chain";
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  const chainKey = chain.id ?? chainAccess.id ?? name;
  const logoCandidates = resolveChainLogoCandidates(chain);
  const [logoFailed, setLogoFailed] = useState(false);
  const [logoIndex, setLogoIndex] = useState(0);
  const logoUrl = logoCandidates[logoIndex] || resolveChainLogoUrl(chain);

  useEffect(() => {
    setLogoFailed(false);
    setLogoIndex(0);
  }, [chainKey, logoCandidates.length]);

  const showLogo = logoUrl && !logoFailed;

  return (
    <div className="flex min-h-[52px] w-full min-w-0 flex-row items-center gap-3 rounded-sm bg-[#1f1f1f] px-3 py-1.5 shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] transition-colors hover:border-white/14 hover:bg-[#242424]">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/[0.06] bg-[#141414]">
        {showLogo ? (
          <img
            src={logoUrl}
            alt={name}
            className="h-8 w-8 object-contain p-0.5 rounded-full"
            loading="lazy"
            decoding="async"
            onError={() => {
              const next = logoIndex + 1;
              if (next < logoCandidates.length) {
                setLogoIndex(next);
                return;
              }
              setLogoFailed(true);
            }}
          />
        ) : (
          <span className="font-title text-sm font-semibold text-white">
            {initial}
          </span>
        )}
      </div>
      <span
        className="font-secondary min-w-0 flex-1 text-left text-[13px] font-medium leading-tight text-white sm:text-sm"
        title={name}
      >
        <span className="line-clamp-2">{name}</span>
      </span>
    </div>
  );
}

const CREATE_PANEL_MOUNT_ID = "dashboard-api-key-create-mount";

const CREATE_PANEL_VARIANTS = {
  initial: { opacity: 0, y: -16 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

const ApiKeyManager = forwardRef(function ApiKeyManager(
  { project, onSelectKey, embedded = false, onApiKeysCountChange },
  ref,
) {
  const { currentOrganization } = useOrganization();
  const [apiKeys, setApiKeys] = useState([]);
  const [availableChains, setAvailableChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createErrorSubscription, setCreateErrorSubscription] = useState(false);
  const createPanelRef = useRef(null);
  const createKeyNameInputRef = useRef(null);
  const endpointExamplesSectionRef = useRef(null);
  const [newKeyData, setNewKeyData] = useState({ name: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState(null);
  const [rotatingKeyId, setRotatingKeyId] = useState(null);
  const [updatingKeyId, setUpdatingKeyId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [isApiKeysListExpanded, setIsApiKeysListExpanded] = useState(true);
  const [expandedKeyCards, setExpandedKeyCards] = useState({});
  const [networksExpanded, setNetworksExpanded] = useState(false);
  const [endpointExamplesOpen, setEndpointExamplesOpen] = useState(false);
  const [endpointExampleSearch, setEndpointExampleSearch] = useState("");
  const [showEditKeyModal, setShowEditKeyModal] = useState(false);
  // Per-key reveal state — Set of apiKey.id whose value is currently shown
  // in plain text. Keys are masked by default to keep them safe during
  // screen-shares; copy still grabs the real key regardless.
  const [revealedKeys, setRevealedKeys] = useState(() => new Set());
  const toggleKeyRevealed = (id) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [editKeyData, setEditKeyData] = useState({ name: "" });

  const loadData = useCallback(async () => {
    if (!currentOrganization?.id || !project?.id) return;
    setLoading(true);
    try {
      const [keys, projectDetails] = await Promise.all([
        apiService.getApiKeys(currentOrganization.id, project.uuid),
        apiService.getProject(project.uuid),
      ]);
      const normalizedKeys = Array.isArray(keys) ? keys : [];
      setApiKeys(normalizedKeys);
      onApiKeysCountChange?.(normalizedKeys.length);
      const projectChains = projectDetails.chain_access || [];
      setAvailableChains(projectChains);
    } catch {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id, project?.id, onApiKeysCountChange]);

  useEffect(() => {
    if (currentOrganization && project) {
      loadData();
    }
  }, [currentOrganization, project, loadData]);

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    if (!currentOrganization || !project) return;

    setIsSubmitting(true);
    try {
      const newKey = await apiService.createApiKey({
        name: newKeyData.name,
        organization: currentOrganization.id,
        project: project.id,
      });
      setCreateError(null);
      setCreateErrorSubscription(false);
      setNewKeyData({ name: "" });
      // Optimistic state update — append the new key locally BEFORE the
      // re-fetch. Two reasons:
      //   1. Instant feedback — the new key appears in the list without
      //      waiting for the second round-trip.
      //   2. Backstop against the backend list cache returning a stale
      //      0-key snapshot (`apikeys:list:<user>:<project>:<org>` had a
      //      30s TTL with broken invalidation — fixed server-side but
      //      the optimistic update means the UI is correct even when
      //      the cache regresses).
      if (newKey?.id || newKey?.uuid) {
        setApiKeys((prev) => {
          // Dedup: if loadData already raced and inserted, don't double-add.
          const exists = prev.some(
            (k) => (k.id && k.id === newKey.id) || (k.uuid && k.uuid === newKey.uuid),
          );
          return exists ? prev : [...prev, newKey];
        });
      }
      // Re-fetch in the background to reconcile with server truth.
      loadData();
      setShowCreatePanel(false);
      setNewlyCreatedKey(newKey);
    } catch (error) {
      setCreateError(getCreateApiKeyErrorMessage(error));
      setCreateErrorSubscription(isSubscriptionRequiredError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteApiKey = async (apiKey) => {
    if (!apiKey?.id) return;
    setDeletingKeyId(apiKey.id);
    try {
      await apiService.deleteApiKey(apiKey.uuid);
      toast.success(`API key "${apiKey.name}" deleted successfully`);
      await loadData();
    } catch (error) {
      toast.error("Failed to delete API key");
    } finally {
      setDeletingKeyId(null);
    }
  };

  // Rotate (regenerate) the API key value. Backend updates the same row
  // — analytics history (ApiKeyUsageHourly/Daily/Monthly) is preserved
  // because the FK is on api_key_id, not on the key string. The OLD
  // value stops working the instant the backend saves. We reuse
  // `newlyCreatedKey` state so the existing "fresh key" reveal panel
  // surfaces the new value with the same copy+warn UX as creation.
  const handleRotateApiKey = async (apiKey) => {
    if (!apiKey?.id) return;
    setRotatingKeyId(apiKey.id);
    try {
      const updated = await apiService.rotateApiKey(apiKey.uuid);
      if (updated?.key) {
        setNewlyCreatedKey(updated);
        setRevealedKeys((prev) => new Set([...prev, updated.id]));
      }
      toast.success(`API key "${apiKey.name}" rotated. Copy the new value now — it won't be shown again.`);
      await loadData();
    } catch (error) {
      toast.error(error?.message || "Failed to rotate API key");
    } finally {
      setRotatingKeyId(null);
    }
  };

  const startEditApiKey = (apiKey) => {
    if (!apiKey?.id) return;
    setEditingApiKey(apiKey);
    setEditKeyData({ name: apiKey.name || "" });
    setShowEditKeyModal(true);
  };

  const handleEditApiKey = async (e) => {
    e.preventDefault();
    if (!editingApiKey?.id || updatingKeyId) return;

    const nextName = editKeyData.name.trim();
    if (!nextName) {
      toast.error("API key name cannot be empty");
      return;
    }

    if (nextName === (editingApiKey.name || "").trim()) {
      setShowEditKeyModal(false);
      setEditingApiKey(null);
      return;
    }

    setUpdatingKeyId(editingApiKey.id);
    try {
      const updatedKey = await apiService.updateApiKey(editingApiKey.uuid, {
        name: nextName,
      });
      const mergedKey = updatedKey || { ...editingApiKey, name: nextName };
      setApiKeys((prev) =>
        prev.map((key) =>
          String(key.id) === String(editingApiKey.id) ? mergedKey : key,
        ),
      );
      setNewlyCreatedKey((prev) =>
        prev && String(prev.id) === String(editingApiKey.id) ? mergedKey : prev,
      );
      toast.success("API key updated successfully");
      setShowEditKeyModal(false);
      setEditingApiKey(null);
      setEditKeyData({ name: "" });
    } catch (error) {
      toast.error(error.message || "Failed to update API key");
    } finally {
      setUpdatingKeyId(null);
    }
  };

  const confirmDeleteApiKey = useCallback(
    (apiKey) => {
      if (!apiKey?.id || deletingKeyId === apiKey.id) return;

      toast.custom(
        (t) => (
          <div
            className={`pointer-events-auto w-[min(92vw,30rem)] rounded-sm border border-red-500/30 bg-[#111111] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.45)] transition-all duration-200 ${
              t.visible
                ? "translate-y-0 opacity-100"
                : "-translate-y-2 opacity-0"
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-red-500/25 bg-red-500/10 text-red-300">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-title text-base font-semibold text-white">
                  Delete API key?
                </p>
                <p className="font-secondary mt-1 text-sm leading-relaxed text-white/70">
                  <span className="font-semibold text-white">
                    {apiKey.name}
                  </span>{" "}
                  will be removed permanently. This action cannot be undone.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => toast.dismiss(t.id)}
                    className="font-secondary rounded-sm border border-white/15 bg-transparent px-3 py-2 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      toast.dismiss(t.id);
                      handleDeleteApiKey(apiKey);
                    }}
                    className="font-secondary rounded-sm border border-red-500/30 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-200 transition-colors hover:bg-red-500/25"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ),
        {
          id: `delete-api-key-${apiKey.id}`,
          duration: Infinity,
          position: "top-center",
        },
      );
    },
    [deletingKeyId, handleDeleteApiKey],
  );

  const confirmRotateApiKey = useCallback(
    (apiKey) => {
      if (!apiKey?.id || rotatingKeyId === apiKey.id) return;
      toast.custom(
        (t) => (
          <div
            className={`pointer-events-auto w-[min(92vw,30rem)] rounded-sm border border-amber-500/30 bg-[#111111] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.45)] transition-all duration-200 ${
              t.visible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-amber-500/25 bg-amber-500/10 text-amber-300">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-title text-base font-semibold text-white">
                  Rotate API key?
                </p>
                <p className="font-secondary mt-1 text-sm leading-relaxed text-white/70">
                  <span className="font-semibold text-white">{apiKey.name}</span>'s current key value will stop working immediately and a new one will be generated. Make sure you can update wherever the old key is in use.
                </p>
                <p className="font-secondary mt-2 text-xs leading-relaxed text-amber-200/85">
                  Analytics history (requests, daily/monthly counters) is preserved — only the key string changes.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => toast.dismiss(t.id)}
                    className="font-secondary rounded-sm border border-white/15 bg-transparent px-3 py-2 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      toast.dismiss(t.id);
                      handleRotateApiKey(apiKey);
                    }}
                    className="font-secondary rounded-sm border border-amber-500/30 bg-amber-500/15 px-3 py-2 text-sm font-semibold text-amber-200 transition-colors hover:bg-amber-500/25"
                  >
                    Rotate
                  </button>
                </div>
              </div>
            </div>
          </div>
        ),
        { id: `rotate-api-key-${apiKey.id}`, duration: Infinity, position: "top-center" },
      );
    },
    [rotatingKeyId, handleRotateApiKey],
  );

  const copyToClipboard = async (text, keyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(keyId);
      toast.success("API Key copied to clipboard");
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // RPC Gateway endpoints
  const RPC_GATEWAY_URL =
    process.env.REACT_APP_RPC_GATEWAY_URL || "http://localhost:8545";
  const WS_GATEWAY_URL =
    process.env.REACT_APP_WS_GATEWAY_URL || "ws://localhost:8545";

  // Chain type constants matching backend
  const CHAIN_TYPES = {
    evm: { label: "EVM JSON-RPC", color: "blue" },
    evm_ws: { label: "EVM WebSocket", color: "green" },
    tendermint: { label: "Tendermint RPC", color: "purple" },
    tendermint_rest: { label: "Tendermint REST", color: "indigo" },
    tendermint_grpc: { label: "Tendermint gRPC", color: "violet" },
  };

  const getCurlExample = (chain, apiKey) => {
    const slug = chain.slug || chain.chain_slug || "unknown";
    const chainType = chain.chain_type || "evm";

    // For WebSocket, derive the HTTP chain slug (remove -ws suffix)
    const httpChainSlug = slug.replace(/-ws$/, "");

    // If chain has custom example_curl from admin, use it
    if (chain.example_curl) {
      return chain.example_curl
        .replace(/\{api_key\}/g, apiKey)
        .replace(/\{chain_slug\}/g, slug)
        .replace(/\{http_chain_slug\}/g, httpChainSlug)
        .replace(/\{rpc_gateway\}/g, RPC_GATEWAY_URL)
        .replace(/\{ws_gateway\}/g, WS_GATEWAY_URL)
        .replace(/YOUR_API_KEY/g, apiKey);
    }

    // Generate default examples based on chain type
    switch (chainType) {
      case "evm_ws":
        return `// JavaScript WebSocket Example
const ws = new WebSocket("${WS_GATEWAY_URL}/");

// For Node.js with ws library (supports headers):
// const WebSocket = require('ws');
// const ws = new WebSocket("${WS_GATEWAY_URL}/", {
//   headers: {
//     "x-api-key": "${apiKey}",
//     "x-chain": "${httpChainSlug}"
//   }
// });

ws.onopen = () => {
  console.log("Connected to ${chain.name}");
  
  // Subscribe to new block headers
  ws.send(JSON.stringify({
    jsonrpc: "2.0",
    method: "eth_subscribe",
    params: ["newHeads"],
    id: 1
  }));
};

ws.onmessage = (event) => {
  const data = JSON.parse(event.data);
  console.log("New block:", data);
};

ws.onerror = (error) => {
  console.error("WebSocket error:", error);
};`;

      case "tendermint":
        return `# Get node status
curl "${RPC_GATEWAY_URL}/${slug}/${apiKey}/status"

# Get latest block
curl "${RPC_GATEWAY_URL}/${slug}/${apiKey}/block"`;

      case "tendermint_rest":
        return `# Tendermint REST API
# Note: REST endpoints vary by chain - check documentation
curl "${RPC_GATEWAY_URL}/${slug}/${apiKey}/cosmos/base/tendermint/v1beta1/blocks/latest"`;

      case "tendermint_grpc":
        return `# Tendermint gRPC
# Use grpcurl or a gRPC client
# Endpoint: ${RPC_GATEWAY_URL}
# Headers: x-api-key: ${apiKey}, x-chain: ${slug}`;

      case "evm":
      default:
        return `curl -X POST "${RPC_GATEWAY_URL}/${slug}/${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","method":"eth_blockNumber","params":[],"id":1}'`;
    }
  };

  const getChainTypeInfo = (chain) => {
    const chainType = chain.chain_type || "evm";
    return CHAIN_TYPES[chainType] || CHAIN_TYPES["evm"];
  };

  const maskApiKeyDisplay = (key) => {
    if (!key || typeof key !== "string") return "••••••••";
    if (key.length <= 18) return `${key.slice(0, 8)}…`;
    return `${key.slice(0, 14)}…${key.slice(-4)}`;
  };

  const getChainTypeBadgeColor = (chainType) => {
    switch (chainType) {
      case "evm_ws":
        return "rounded-sm border border-emerald-500/25 bg-emerald-500/10 text-emerald-300";
      case "tendermint":
      case "tendermint_rest":
      case "tendermint_grpc":
        return "rounded-sm border border-primary/25 bg-primary/10 text-primary";
      case "evm":
      default:
        return "rounded-sm border border-primary/25 bg-primary/15 text-primary";
    }
  };

  const toggleApiKeyCardExpanded = useCallback((keyId) => {
    setExpandedKeyCards((prev) => ({
      ...prev,
      [keyId]: !prev[keyId],
    }));
  }, []);

  const closeCreatePanel = useCallback(() => {
    setShowCreatePanel(false);
    setCreateError(null);
    setCreateErrorSubscription(false);
    setNewlyCreatedKey(null);
  }, []);

  const handleCreateButtonClick = useCallback(() => {
    if (loading) return;
    if (apiKeys.length >= 3) {
      toast.error(
        "Maximum of 3 API keys per project reached. Please delete an existing key to create a new one.",
        {
          duration: 5000,
          icon: "⚠️",
        },
      );
      return;
    }
    setCreateError(null);
    setCreateErrorSubscription(false);
    setNewlyCreatedKey(null);
    setShowCreatePanel((open) => !open);
  }, [loading, apiKeys.length]);

  useImperativeHandle(
    ref,
    () => ({
      openCreatePanel: () => {
        if (loading) return;
        if (apiKeys.length >= 3) {
          toast.error(
            "Maximum of 3 API keys per project reached. Please delete an existing key to create a new one.",
            {
              duration: 5000,
              icon: "⚠️",
            },
          );
          return;
        }
        setCreateError(null);
        setCreateErrorSubscription(false);
        setNewlyCreatedKey(null);
        setShowCreatePanel(true);
      },
      closeCreatePanel,
    }),
    [loading, apiKeys.length, closeCreatePanel],
  );

  const createPanelVisible = showCreatePanel || newlyCreatedKey;

  useEffect(() => {
    if (!createPanelVisible) return;
    const onDown = (e) => {
      if (createPanelRef.current?.contains(e.target)) return;
      if (e.target.closest?.("[data-create-api-key-trigger]")) return;
      closeCreatePanel();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [createPanelVisible, closeCreatePanel]);

  useEffect(() => {
    if (!createPanelVisible) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeCreatePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createPanelVisible, closeCreatePanel]);

  const [createPanelHost, setCreatePanelHost] = useState(null);
  const enabledChainsCount = availableChains.filter((ca) => ca.enabled).length;

  useLayoutEffect(() => {
    if (!embedded) {
      setCreatePanelHost(null);
      return;
    }
    setCreatePanelHost(document.getElementById(CREATE_PANEL_MOUNT_ID));
  }, [embedded]);

  useEffect(() => {
    if (!showCreatePanel || newlyCreatedKey) return;
    const id = requestAnimationFrame(() => {
      createKeyNameInputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [showCreatePanel, newlyCreatedKey]);

  const handleViewEndpointExamples = useCallback(() => {
    setEndpointExamplesOpen(true);
    setNewlyCreatedKey(null);

    requestAnimationFrame(() => {
      endpointExamplesSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const renderCreatePanelBar = () => (
    <div className="relative w-full">
      <AnimatePresence mode="sync">
        {createPanelVisible && (
          <motion.div
            key="create-api-key-bar"
            ref={createPanelRef}
            role="dialog"
            aria-modal="false"
            aria-labelledby={
              newlyCreatedKey
                ? "create-api-key-success-title"
                : "create-api-key-title"
            }
            className="relative w-full border-b border-white/10 bg-landing-section"
            variants={CREATE_PANEL_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div className="max-h-[min(85vh,720px)] overflow-y-auto px-4 py-5 sm:px-8 sm:py-6">
              {newlyCreatedKey ? (
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-500/25 bg-emerald-500/10 text-emerald-300">
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <h3
                          id="create-api-key-success-title"
                          className="font-title text-lg font-bold tracking-tight text-white sm:text-xl"
                        >
                          API key ready
                        </h3>
                        <p className="font-secondary mt-1 text-sm text-white/65">
                          Copy and store this key now. For security reasons,
                          this is the only moment the full value is shown.
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span className="font-secondary inline-flex items-center rounded-sm border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300">
                            Active immediately
                          </span>
                          <span className="font-secondary inline-flex items-center rounded-sm border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs font-medium text-white/65">
                            {enabledChainsCount} network
                            {enabledChainsCount === 1 ? "" : "s"} available
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={closeCreatePanel}
                      className="shrink-0 rounded-sm p-1.5 text-white/45 transition-colors hover:bg-white/[0.06] hover:text-white"
                      aria-label="Close"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-secondary text-xs uppercase tracking-[0.16em] text-white/40">
                          {newlyCreatedKey.name}
                        </p>
                        <p className="font-secondary mt-1 text-sm text-white/65">
                          Store this key in your app or secret manager before
                          closing this message.
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(newlyCreatedKey.key, "new-key")
                          }
                          className="font-secondary rounded-sm border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/15"
                        >
                          {copiedKey === "new-key" ? "Copied!" : "Copy Key"}
                        </button>
                      </div>
                    </div>
                    <code className="block break-all font-mono text-sm text-emerald-400">
                      {newlyCreatedKey.key}
                    </code>
                  </div>

                  <div className="rounded-sm border border-white/10 bg-white/[0.03] p-4">
                    <p className="font-secondary text-sm leading-relaxed text-white/70">
                      Connection examples are still available below in
                      <span className="font-semibold text-white">
                        {" "}
                        endpoint examples
                      </span>
                      , but they are no longer expanded automatically after
                      creating a key.
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-white/10 pt-4 sm:flex-row sm:justify-end">
                    {enabledChainsCount > 0 ? (
                      <button
                        type="button"
                        onClick={handleViewEndpointExamples}
                        className="font-secondary rounded-sm border border-white/15 bg-transparent px-5 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                      >
                        View Endpoint Examples
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={closeCreatePanel}
                      className="font-secondary rounded-sm border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleCreateApiKey} className="space-y-5">
                  {createError && createErrorSubscription && (
                    <div
                      className="font-secondary rounded-sm border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"
                      role="alert"
                    >
                      <p className="font-semibold text-amber-50">
                        An active plan is required
                      </p>
                      <p className="mt-1 text-amber-100/90">{createError}</p>
                      <p className="mt-3">
                        <Link
                          to="/settings"
                          className="font-semibold text-primary underline-offset-2 hover:underline"
                        >
                          Open Subscription → choose a plan
                        </Link>
                      </p>
                    </div>
                  )}
                  {createError && !createErrorSubscription && (
                    <div
                      className="font-secondary rounded-sm border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                      role="alert"
                    >
                      {createError}
                    </div>
                  )}
                  <h3
                    id="create-api-key-title"
                    className="font-title shrink-0 text-lg font-bold tracking-tight text-white sm:text-xl"
                  >
                    Create API Key
                  </h3>
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:gap-8">
                    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                      <label
                        htmlFor="key-name"
                        className="font-secondary shrink-0 text-sm font-medium text-white/80 sm:pt-0.5"
                      >
                        Key name
                      </label>
                      <input
                        ref={createKeyNameInputRef}
                        type="text"
                        id="key-name"
                        required
                        value={newKeyData.name}
                        onChange={(e) =>
                          setNewKeyData({ ...newKeyData, name: e.target.value })
                        }
                        className="font-secondary min-h-[44px] w-full min-w-0 flex-1 rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-[15px] text-white transition-colors placeholder:text-white/40 hover:border-white/25 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                        placeholder="Production API Key"
                        autoComplete="off"
                      />
                    </div>
                    <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
                      <button
                        type="button"
                        onClick={closeCreatePanel}
                        className="font-secondary rounded-sm border border-white/15 bg-transparent px-4 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="font-secondary inline-flex min-h-[44px] min-w-[10rem] items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <>
                            <span
                              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#0a0a0a] border-t-transparent"
                              aria-hidden
                            />
                            Creating...
                          </>
                        ) : (
                          "Create API Key"
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="font-secondary border-t border-white/10 pt-4 text-sm leading-relaxed text-white/65">
                    <span className="font-semibold text-primary">Note:</span>{" "}
                    This API key will have access to all blockchain networks
                    available to your organization.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header — dashboard layout provides title + Create when embedded */}
      {!embedded && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h3 className="font-title text-lg font-bold tracking-tight text-white sm:text-xl">
                API Keys
              </h3>
              <span
                className={`font-secondary rounded-sm border px-2 py-0.5 text-xs font-semibold sm:px-3 sm:py-1 sm:text-sm ${
                  loading
                    ? "border-white/15 bg-white/[0.04] text-white/55"
                    : apiKeys.length >= 3
                      ? "border-red-500/35 bg-red-500/10 text-red-300"
                      : "border-primary/35 bg-primary/10 text-primary"
                }`}
              >
                {loading ? "…/3" : `${apiKeys.length}/3`}
              </span>
            </div>
            <p className="font-secondary mt-1 text-sm text-white/65">
              Manage your high-performance RPC endpoints.
            </p>
          </div>
          <motion.button
            type="button"
            data-create-api-key-trigger
            onClick={handleCreateButtonClick}
            className={`font-secondary inline-flex items-center justify-center gap-2 rounded-sm border px-4 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors sm:text-[15px] ${
              apiKeys.length >= 3
                ? "cursor-not-allowed border-white/10 bg-white/10 text-white/40"
                : "border-primary bg-primary hover:bg-primary-hover"
            }`}
            whileHover={apiKeys.length < 3 ? { scale: 1.02 } : {}}
            whileTap={apiKeys.length < 3 ? { scale: 0.98 } : {}}
          >
            <svg
              className="h-4 w-4 sm:h-5 sm:w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Create API Key
          </motion.button>
        </div>
      )}

      {/* Available Chains — static grid, 4 rows when collapsed, Show more / Show less */}
      {availableChains.length > 0 &&
        (() => {
          const enabledChains = availableChains.filter((ca) => ca.enabled);
          if (enabledChains.length === 0) return null;
          const hasOverflow = enabledChains.length > NETWORKS_COLLAPSED_COUNT;
          const displayed =
            networksExpanded || !hasOverflow
              ? enabledChains
              : enabledChains.slice(0, NETWORKS_COLLAPSED_COUNT);
          return (
            <div className="">
              <h4 className="font-title mb-4 flex items-center gap-2 text-2xl font-semibold tracking-tight text-white">
                Available Blockchain Networks
                <span className="font-secondary inline-flex items-center justify-center rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-white/80">
                  {enabledChains.length}
                </span>
              </h4>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 sm:gap-4">
                {displayed.map((chainAccess) => {
                  const chain =
                    chainAccess.chain_details || chainAccess.chain || {};
                  const id = chain.id ?? chainAccess.id;
                  return (
                    <AvailableChainCard
                      key={id ?? chainAccess.id}
                      chainAccess={chainAccess}
                    />
                  );
                })}
              </div>
              {hasOverflow ? (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setNetworksExpanded((v) => !v)}
                    className="font-secondary rounded-sm bg-transparent py-2 text-sm font-medium text-white transition-colors hover:border-white/20 hover:underline"
                  >
                    {networksExpanded ? "Show less" : "Show more"}
                  </button>
                </div>
              ) : null}
            </div>
          );
        })()}

      {/* API Keys List */}
      {apiKeys.length > 0 ? (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex flex-row w-full justify-between gap-4">
              <div className="space-y-4 sm:space-y-5 w-full">
                {apiKeys.map((apiKey) => {
                  const isCardOpen = !!expandedKeyCards[apiKey.id];
                  return (
                    <motion.div
                      key={apiKey.id}
                      className="overflow-hidden"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 rounded-sm bg-[#1a1a1a] transition-colors hover:border-white/15 mb-4">
                        <div className="font-secondary flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left sm:items-center sm:px-5 sm:py-4">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-title truncate text-base font-semibold text-white sm:text-lg">
                              {apiKey.name}
                            </h4>
                            <p className="font-secondary mt-0.5 text-xs text-white/50 sm:text-sm">
                              Created{" "}
                              {new Date(apiKey.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 px-4 pb-3 sm:justify-end sm:px-5 sm:pb-0 sm:pl-0">
                          <span
                            className={`font-secondary inline-flex items-center rounded-sm px-2.5 py-2.5 text-xs font-medium uppercase tracking-widest text-center ${
                              apiKey.is_active
                                ? "border-primary/30 bg-primary/15 text-primary"
                                : "border-white/25 bg-white/10 text-white/40"
                            }`}
                          >
                            {apiKey.is_active ? "Active" : "Inactive"}
                          </span>
                          <motion.button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditApiKey(apiKey);
                            }}
                            disabled={
                              deletingKeyId === apiKey.id ||
                              updatingKeyId === apiKey.id
                            }
                            className="rounded-sm p-2 text-white/45 transition-colors hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            aria-label="Edit API key"
                          >
                            <svg
                              className="h-4 w-4 sm:h-5 sm:w-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-2.586-9.414a2 2 0 112.828 2.828L11 16H8v-3l8.414-8.414z"
                              />
                            </svg>
                          </motion.button>
                          {/* Rotate — generate a new key value, kill the
                              old one. Analytics history preserved (same FK). */}
                          <motion.button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmRotateApiKey(apiKey);
                            }}
                            disabled={
                              rotatingKeyId === apiKey.id ||
                              deletingKeyId === apiKey.id ||
                              updatingKeyId === apiKey.id
                            }
                            className="rounded-sm p-2 text-white/45 transition-colors hover:bg-amber-500/10 hover:text-amber-300 disabled:cursor-not-allowed disabled:opacity-40"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            aria-label="Rotate API key"
                          >
                            <svg
                              className="h-4 w-4 sm:h-5 sm:w-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteApiKey(apiKey);
                            }}
                            disabled={
                              deletingKeyId === apiKey.id ||
                              updatingKeyId === apiKey.id
                            }
                            className="rounded-sm p-2 text-white/45 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-40"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            aria-label="Delete API key"
                          >
                            <svg
                              className="h-4 w-4 sm:h-5 sm:w-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </motion.button>
                        </div>
                      </div>

                      {/* Key display — masked by default, Show toggles plain
                          text. Copy always grabs the real key regardless of
                          reveal state. Defensive against screen-shares. */}
                      <div className="">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                          <code className="min-w-0 flex-1 truncate rounded-sm bg-primary/5 border border-primary/20 px-4 py-3 font-mono text-sm text-primary">
                            {revealedKeys.has(apiKey.id)
                              ? apiKey.key
                              : maskApiKeyDisplay(apiKey.key)}
                          </code>
                          <motion.button
                            type="button"
                            onClick={() => toggleKeyRevealed(apiKey.id)}
                            className="font-secondary inline-flex shrink-0 items-center justify-center gap-2 rounded-sm border border-white/15 bg-transparent px-4 py-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white sm:min-w-[110px]"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            aria-label={revealedKeys.has(apiKey.id) ? "Hide API key" : "Show API key"}
                            aria-pressed={revealedKeys.has(apiKey.id)}
                          >
                            {revealedKeys.has(apiKey.id) ? (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.86 19.86 0 015.06-5.94M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.5 19.5 0 01-3.17 4.19M14.12 14.12a3 3 0 11-4.24-4.24M1 1l22 22" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                              </svg>
                            )}
                            {revealedKeys.has(apiKey.id) ? "Hide" : "Show"}
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={() =>
                              copyToClipboard(apiKey.key, apiKey.id)
                            }
                            className="font-secondary inline-flex shrink-0 items-center justify-center gap-2 rounded-sm px-5
                            py-3 text-sm font-bold text-[#0a0a0a] transition-colors sm:min-w-[120px] bg-primary hover:bg-primary-hover"
                            whileHover={{
                              scale: 1.02,
                              filter: "brightness(1.06)",
                            }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                            {copiedKey === apiKey.id ? "Copied" : "Copy Key"}
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                <div>
                  {embedded && apiKeys.length > 0 && (
                    <div className="mt-4">
                      <DashboardInsights apiKeys={apiKeys} />
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full max-w-[400px] relative">
                <DashboardUsageStatistics apiKeys={apiKeys} />
              </div>
            </div>
            {/* Endpoint examples: all enabled project chains; cURL uses first API key */}
            {availableChains.filter((ca) => ca.enabled).length > 0 ? (
              <div ref={endpointExamplesSectionRef} className="py-6">
                <button
                  type="button"
                  onClick={() => setEndpointExamplesOpen((open) => !open)}
                  className="font-title font-medium flex items-center text-base text-white/75 transition-colors hover:text-primary/80"
                >
                  <svg
                    className={`mr-1 h-4 w-4 transition-transform ${endpointExamplesOpen ? "rotate-90" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  {endpointExamplesOpen ? "Hide" : "Show"} endpoint examples
                </button>

                <AnimatePresence>
                  {endpointExamplesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 space-y-3 overflow-hidden"
                    >
                      {(() => {
                        const exampleKey = apiKeys[0]?.key ?? "YOUR_API_KEY";
                        const enabledList = availableChains.filter(
                          (ca) => ca.enabled,
                        );
                        const q = endpointExampleSearch.trim().toLowerCase();
                        const filtered = q
                          ? enabledList.filter((chainAccess) => {
                              const chain =
                                chainAccess.chain_details ||
                                chainAccess.chain ||
                                {};
                              const slug = String(
                                chain.slug || chainAccess.slug || "",
                              ).toLowerCase();
                              const name = String(
                                chain.name || chainAccess.name || "",
                              ).toLowerCase();
                              const label =
                                getChainTypeInfo(chain).label.toLowerCase();
                              const body = getCurlExample(
                                chain,
                                exampleKey,
                              ).toLowerCase();
                              return (
                                name.includes(q) ||
                                slug.includes(q) ||
                                label.includes(q) ||
                                body.includes(q)
                              );
                            })
                          : enabledList;

                        return (
                          <>
                            <div className="mb-1">
                              <label
                                htmlFor="endpoint-example-search"
                                className="font-secondary mb-2 block text-xs text-white/50"
                              >
                                Search examples
                              </label>
                              <input
                                id="endpoint-example-search"
                                type="search"
                                value={endpointExampleSearch}
                                onChange={(e) =>
                                  setEndpointExampleSearch(e.target.value)
                                }
                                placeholder="Network name, slug, protocol, or request text…"
                                autoComplete="off"
                                className="font-secondary w-full rounded-sm border border-white/[0.1] bg-[#0a0a0a] px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </div>
                            {filtered.length === 0 ? (
                              <p className="font-secondary rounded-sm border border-white/10 bg-[#0c0c0c] px-3 py-6 text-center text-sm text-white/45">
                                No examples match your search.
                              </p>
                            ) : (
                              filtered.map((chainAccess) => {
                                const chain =
                                  chainAccess.chain_details ||
                                  chainAccess.chain ||
                                  {};
                                const slug = chain.slug || chainAccess.slug;
                                const name =
                                  chain.name || chainAccess.name || "Unknown";
                                const chainTypeInfo = getChainTypeInfo(chain);
                                const copyId = `endpoint-${
                                  chain.id ?? chainAccess.id ?? slug ?? name
                                }`;

                                return (
                                  <div
                                    key={copyId}
                                    className="rounded-sm border border-white/10 bg-[#0c0c0c] p-3"
                                  >
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                      <div className="flex min-w-0 items-center gap-2">
                                        <span className="font-secondary truncate text-xs text-white/60">
                                          {name}
                                        </span>
                                        <span
                                          className={`font-secondary shrink-0 px-2 py-0.5 text-xs ${getChainTypeBadgeColor(chain.chain_type)}`}
                                        >
                                          {chainTypeInfo.label}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          copyToClipboard(
                                            getCurlExample(chain, exampleKey),
                                            copyId,
                                          )
                                        }
                                        className="font-secondary shrink-0 text-xs text-primary hover:text-primary/80"
                                      >
                                        {copiedKey === copyId
                                          ? "Copied!"
                                          : "Copy example"}
                                      </button>
                                    </div>
                                    <div className="mb-3 rounded-sm border border-white/[0.06] bg-[#0a0a0a] p-2">
                                      <div className="font-secondary mb-1 text-xs text-white/45">
                                        RPC Gateway:
                                      </div>
                                      <code className="break-all font-mono text-xs text-primary">
                                        {RPC_GATEWAY_URL}
                                      </code>
                                    </div>
                                    <div className="font-secondary mb-1 text-xs text-white/45">
                                      Example request:
                                    </div>
                                    <code className="block whitespace-pre-wrap break-all font-mono text-xs text-white/80">
                                      {getCurlExample(chain, exampleKey)}
                                    </code>
                                  </div>
                                );
                              })
                            )}
                          </>
                        );
                      })()}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <div className="rounded-sm border border-white/10 bg-landing-section px-6 py-12 text-center sm:px-10">
          <svg
            className="mx-auto h-12 w-12 text-primary/45"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
            />
          </svg>
          <h3 className="font-title mt-4 text-lg font-bold tracking-tight text-white">
            No API keys
          </h3>
          <p className="font-secondary mt-2 text-[15px] leading-relaxed text-white/65">
            Get started by creating your first API key.
          </p>
          <div className="mt-8">
            <motion.button
              onClick={handleCreateButtonClick}
              data-create-api-key-trigger
              type="button"
              className="font-secondary inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Create API Key
            </motion.button>
          </div>
        </div>
      )}

      {embedded && createPanelHost
        ? createPortal(renderCreatePanelBar(), createPanelHost)
        : !embedded
          ? renderCreatePanelBar()
          : null}

      <AnimatePresence>
        {showEditKeyModal && editingApiKey && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (updatingKeyId) return;
              setShowEditKeyModal(false);
              setEditingApiKey(null);
              setEditKeyData({ name: "" });
            }}
          >
            <motion.div
              className="w-full max-w-md rounded-sm border border-white/10 bg-landing-section shadow-2xl"
              initial={{ scale: 0.96, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 12 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-white/10 p-6">
                <h3 className="font-title text-xl font-bold text-white">
                  Edit API key
                </h3>
                <p className="font-secondary mt-2 text-sm text-white/60">
                  Rename this key without changing its secret value.
                </p>
              </div>
              <form onSubmit={handleEditApiKey} className="space-y-4 p-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/70">
                    API key name
                  </label>
                  <input
                    type="text"
                    required
                    value={editKeyData.name}
                    onChange={(e) =>
                      setEditKeyData({ name: e.target.value })
                    }
                    placeholder="My API key"
                    className="font-secondary w-full rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/35 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                    autoFocus
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (updatingKeyId) return;
                      setShowEditKeyModal(false);
                      setEditingApiKey(null);
                      setEditKeyData({ name: "" });
                    }}
                    className="font-secondary rounded-sm border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:bg-white/[0.06]"
                    disabled={!!updatingKeyId}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!!updatingKeyId}
                    className="font-secondary rounded-sm border border-primary bg-primary px-4 py-2.5 text-sm font-bold text-[#0a0a0a] hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {updatingKeyId ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default ApiKeyManager;
